import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Switch,
  Table,
  Typography,
} from '@mui/joy';
import { Wallet as WalletIcon, Plus, ArrowClockwise } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '../../lib/api';

interface Wallet {
  balance: string;
  auto_reload_enabled: boolean;
  auto_reload_threshold: string;
  auto_reload_amount: string;
}

interface WalletTransaction {
  id: number;
  kind: string;
  kind_display: string;
  category_display: string;
  amount: string;
  balance_after: string;
  description: string;
  created_at: string;
}

const PRESET_AMOUNTS = [25, 50, 100, 250];

const money = (value: string | number) =>
  `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface WalletCardProps {
  /** Only org admins may move money; everyone may see the balance. */
  canManage: boolean;
}

/**
 * Prepaid balance that covers phone numbers, texts, calls, and carrier
 * registration fees.
 *
 * Separate from the seat subscription: the subscription is a fixed monthly
 * charge, this is consumption. Keeping them visually distinct on the same page
 * matters — a contractor whose texts stopped needs to see at a glance that
 * their plan is fine and their balance is not.
 */
export const WalletCard: React.FC<WalletCardProps> = ({ canManage }) => {
  const queryClient = useQueryClient();
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [amount, setAmount] = useState('50');
  const [error, setError] = useState<string | null>(null);

  const { data: wallet, isLoading } = useQuery<Wallet>({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get<Wallet>('/api/wallet/')).data,
  });

  const { data: transactions } = useQuery<{ results?: WalletTransaction[] }>({
    queryKey: ['wallet-transactions'],
    queryFn: async () =>
      (await api.get<{ results?: WalletTransaction[] }>('/api/wallet/transactions/')).data,
  });

  const topUp = useMutation({
    mutationFn: (value: string) => api.post('/api/wallet/top-up/', { amount: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      setTopUpOpen(false);
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.responseData?.error || 'The payment could not be processed.');
    },
  });

  const setAutoReload = useMutation({
    mutationFn: (payload: Partial<Wallet>) =>
      api.patch('/api/wallet/auto-reload/', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallet'] }),
  });

  if (isLoading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const balance = Number(wallet?.balance ?? 0);
  const isEmpty = balance <= 0;
  const isLow = !isEmpty && balance < Number(wallet?.auto_reload_threshold ?? 10);
  const rows = transactions?.results ?? [];

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1}
            sx={{ mb: 2 }}
          >
            <Typography level="title-lg" startDecorator={<WalletIcon size={20} />}>
              Usage Balance
            </Typography>
            {canManage && (
              <Button
                size="sm"
                startDecorator={<Plus size={16} />}
                onClick={() => { setError(null); setTopUpOpen(true); }}
              >
                Add Funds
              </Button>
            )}
          </Stack>

          <Typography level="body-sm" sx={{ mb: 2 }}>
            Covers phone numbers, text messages, calls, and carrier registration
            fees. Separate from your monthly plan.
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
            <Typography level="h2">{money(balance)}</Typography>
            {isEmpty ? (
              <Chip color="danger" variant="soft" size="sm">Empty</Chip>
            ) : isLow ? (
              <Chip color="warning" variant="soft" size="sm">Running low</Chip>
            ) : (
              <Chip color="success" variant="soft" size="sm">Good</Chip>
            )}
          </Stack>

          {isEmpty && (
            <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
              Your balance is empty. You can still receive texts and calls, but
              you can't send or place them until you add funds.
            </Alert>
          )}

          {canManage && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{ flexWrap: 'wrap' }}
              >
                <Box>
                  <Typography
                    level="title-sm"
                    startDecorator={<ArrowClockwise size={16} />}
                  >
                    Automatic reload
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {wallet?.auto_reload_enabled
                      ? `Adds ${money(wallet.auto_reload_amount)} when the balance falls below ${money(wallet.auto_reload_threshold)}.`
                      : 'Off — service stops when the balance runs out.'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(wallet?.auto_reload_enabled)}
                  onChange={(e) =>
                    setAutoReload.mutate({ auto_reload_enabled: e.target.checked } as any)
                  }
                />
              </Stack>
            </>
          )}

          {rows.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Recent activity
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="sm" sx={{ minWidth: 480 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 10).map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Typography level="body-xs">
                            {format(new Date(row.created_at), 'MMM d, h:mm a')}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.description}</Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography
                            level="body-xs"
                            sx={{
                              color: Number(row.amount) < 0 ? 'text.secondary' : 'success.500',
                            }}
                          >
                            {Number(row.amount) < 0 ? '' : '+'}
                            {money(row.amount)}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography level="body-xs">{money(row.balance_after)}</Typography>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Modal open={topUpOpen} onClose={() => setTopUpOpen(false)}>
        <ModalDialog sx={{ width: 420, maxWidth: '95vw' }}>
          <ModalClose />
          <DialogTitle>Add funds</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Charged to the card on file, the same one your monthly plan uses.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {PRESET_AMOUNTS.map((preset) => (
                <Chip
                  key={preset}
                  variant={amount === String(preset) ? 'solid' : 'outlined'}
                  color="primary"
                  onClick={() => setAmount(String(preset))}
                >
                  ${preset}
                </Chip>
              ))}
            </Stack>
            <FormControl>
              <FormLabel>Amount</FormLabel>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                startDecorator="$"
                slotProps={{ input: { min: 10, max: 1000, step: 5 } }}
              />
              <FormHelperText>Between $10 and $1,000.</FormHelperText>
            </FormControl>
            {error && (
              <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button loading={topUp.isPending} onClick={() => topUp.mutate(amount)}>
              Add {money(amount || 0)}
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setTopUpOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default WalletCard;
